import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Spinner } from '../../components/design-system';
import { ALMA_LIMITS } from '../../../supabase/functions/_shared/almaGuards.ts';
import { AdminPage } from './AdminPage';

interface Caller {
  kind: 'user' | 'visitor';
  caller: string;
  name: string | null;
  requests: number;
  tokens: number;
  off_topic: number;
}

interface Usage {
  day: string;
  global_tokens: number;
  callers: Caller[];
}

const fmt = (n: number) => n.toLocaleString('en');

function Bar({ value, max }: { value: number; max: number }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div className="h-1.5 rounded-full bg-app-border overflow-hidden w-24">
      <div className={`h-full rounded-full ${pct >= 100 ? 'bg-crimson' : pct >= 70 ? 'bg-gold' : 'bg-violet'}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Admin view of today's Alma chat load: who is using the most, and who has been paused. */
export function AdminAlmaUsage() {
  const [usage, setUsage] = useState<Usage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const { data, error: err } = await supabase.rpc('admin_alma_usage');
      if (err) setError(err.message);
      else setUsage(data as Usage);
    })();
  }, []);

  const budget = ALMA_LIMITS.public.globalDayTokens;

  return (
    <AdminPage
      title="Alma usage"
      description="Today's chat load (UTC day). Limits are enforced in the database; see docs/alma/README.md for what each one does and how to change it."
    >
      {error && <p className="text-sm text-crimson mb-4">{error}</p>}
      {!usage && !error && <div className="flex justify-center py-16"><Spinner /></div>}
      {usage && (
        <>
          <div className="bg-app-card border border-app-border rounded-xl px-4 py-3 mb-6 max-w-md">
            <div className="text-xs text-app-muted">Signed-out chat, all visitors today</div>
            <div className="font-serif text-[26px] text-app-fg leading-tight">{fmt(usage.global_tokens)} <span className="text-sm text-app-muted">of {fmt(budget)} tokens</span></div>
            <div className="mt-2"><Bar value={usage.global_tokens} max={budget} /></div>
            <p className="text-[11px] text-app-muted mt-2">When this fills up, signed-out visitors can't chat until tomorrow.</p>
          </div>

          {usage.callers.length === 0 ? (
            <p className="text-sm text-app-muted py-8 text-center">No Alma chats yet today.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {usage.callers.map((c) => {
                const limit = c.kind === 'user' ? ALMA_LIMITS.user.dayTokens : ALMA_LIMITS.public.dayTokens;
                const paused = c.off_topic >= ALMA_LIMITS.offTopicStrikes;
                return (
                  <div key={`${c.kind}:${c.caller}`} className="bg-app-card border border-app-border rounded-xl px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2">
                    <div className="min-w-0 flex-1 basis-48">
                      <div className="text-sm text-app-fg truncate">{c.kind === 'user' ? (c.name ?? 'Signed-in user') : 'Signed-out visitor'}</div>
                      <div className="text-[11px] text-app-muted truncate">{c.kind === 'user' ? c.caller : `anonymous ${c.caller.slice(0, 8)}`}</div>
                    </div>
                    <div className="text-xs text-app-fg-2 w-24">{fmt(c.requests)} requests</div>
                    <div className="flex items-center gap-2 text-xs text-app-fg-2">
                      <Bar value={c.tokens} max={limit} />
                      {fmt(c.tokens)} / {fmt(limit)} tokens
                    </div>
                    <div className={`text-xs ${paused ? 'text-crimson font-medium' : c.off_topic > 0 ? 'text-gold' : 'text-app-muted'}`}>
                      {paused ? 'Paused today: off-topic' : `${c.off_topic} off-topic`}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </AdminPage>
  );
}

export default AdminAlmaUsage;
